import { useEffect, useMemo } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";

import ComboBoxMultiple from "components/ComboBoxMultiple";
import FormFieldSpace from "components/FormFields/Space";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import { Space } from "types/generated";
import Card from "ds/components/Card";

import AssignRoleDrawerComboBoxItem from "./ComboBoxItem";
import { CREATE_FORM_ID } from "./constants";
import { AssignRoleFormAddFields } from "./types";
import AssignRoleFormTooltipInfo from "./TooltipInfo";

type AssignRoleDrawerFormAddProps = {
  selectableRoles: Array<{ id: string; name: string }>;
  setIsDirty: (id: string, isDirty: boolean) => void;
  isLoading?: boolean;
  onCreate: (formData: AssignRoleFormAddFields) => void;
  createLoading?: boolean;
  selectableSpaces?: Space[];
};

const AssignRoleDrawerFormAdd = ({
  selectableRoles,
  setIsDirty,
  isLoading,
  onCreate,
  createLoading,
  selectableSpaces,
}: AssignRoleDrawerFormAddProps) => {
  const assignRoleForm = useForm<AssignRoleFormAddFields>({
    defaultValues: {
      roleIds: [],
      space: undefined,
    },
    mode: "onSubmit",
  });

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isDirty },
  } = assignRoleForm;

  const roleIds = watch("roleIds");
  const space = watch("space");

  const hasValues = (roleIds && roleIds.length > 0) || !!space;

  const onSubmit: SubmitHandler<AssignRoleFormAddFields> = (formData) => {
    onCreate(formData);
    reset();
    setIsDirty(CREATE_FORM_ID, false);
  };

  const options = useMemo(
    () => selectableRoles.map(({ id, name }) => ({ value: id, label: name })),
    [selectableRoles]
  );

  // TODO consider add a not existing space option to the list of spaces in case editing an output reference with not existing output
  useEffect(() => {
    setIsDirty(CREATE_FORM_ID, isDirty);
  }, [isDirty, setIsDirty]);

  // Reset form when both fields are empty
  useEffect(() => {
    if (!hasValues && isDirty) {
      // Use setTimeout to defer reset to the next event loop tick
      // This prevents validation errors from appearing briefly when clearing fields
      setTimeout(() => {
        reset();
      }, 0);
    }
  }, [hasValues, isDirty, reset]);

  return (
    <FormProvider {...assignRoleForm}>
      <Card variant="secondary" contentGap="x-large">
        <Controller
          name="roleIds"
          control={control}
          rules={{ required: "Role field is required." }}
          render={({ field, fieldState }) => (
            <ComboBoxMultiple
              tooltipInfo={<AssignRoleFormTooltipInfo />}
              isLoading={isLoading}
              items={options}
              label="Role"
              error={fieldState.error?.message}
              values={field.value}
              onChange={(role) => {
                if (role && field.value.includes(role)) {
                  field.onChange(field.value.filter((r) => r !== role));
                } else {
                  field.onChange([...field.value, role]);
                }
              }}
            >
              {(item) => <AssignRoleDrawerComboBoxItem checkboxVisible id={item.value} {...item} />}
            </ComboBoxMultiple>
          )}
        />
        <FormFieldSpace label="Space" noMargin spaces={selectableSpaces} />

        <Box direction="row" justify="end" gap="medium">
          {hasValues && (
            <Button
              variant="secondary"
              size="small"
              onPress={() => reset()}
              disabled={createLoading}
            >
              Clear
            </Button>
          )}
          <Button
            variant="contrast"
            size="small"
            onPress={() => handleSubmit(onSubmit)()}
            loading={createLoading}
            disabled={createLoading}
          >
            Add
          </Button>
        </Box>
      </Card>
    </FormProvider>
  );
};

export default AssignRoleDrawerFormAdd;
